import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Nav, NavItem } from '@feathr/components';
import { useGoogleAdsPermissions } from '@feathr/extender/hooks';
import { useAccount, useLocalUrl } from '@feathr/extender/state';
import { getEventIdFromUrl } from '@feathr/extender/utils';

function EventMarketingNav(): JSX.Element {
  const localUrl = useLocalUrl();
  const { isPlatformLite, hasMonetization } = useAccount();
  const eventId = getEventIdFromUrl();
  const { t } = useTranslation();
  const showGoogleAds = useGoogleAdsPermissions();

  return (
    <Nav indented={true} label={t('Marketing navigation')} type={'vertical'}>
      <NavItem
        data-command={'true'}
        data-command-description={t('View all campaigns')}
        data-command-keywords={'campaigns,all,marketing'}
        data-command-label={t('All Campaigns')}
        isForwardedFrom={localUrl(`/projects/${eventId}/campaigns`)}
        to={localUrl(`/projects/${eventId}/campaigns/all`)}
      >
        {t('All')}
      </NavItem>
      <NavItem
        data-command={'true'}
        data-command-description={t('Manage ad campaigns')}
        data-command-keywords={'ads,advertising,display,ttd'}
        data-command-label={t('Ad Campaigns')}
        to={localUrl(`/projects/${eventId}/campaigns/ads`)}
      >
        {t('Ads')}
      </NavItem>
      <NavItem
        data-command={'true'}
        data-command-description={t('Manage email campaigns')}
        data-command-keywords={'email,campaigns,newsletters'}
        data-command-label={t('Email Campaigns')}
        to={localUrl(`/projects/${eventId}/campaigns/email`)}
      >
        {t('Email')}
      </NavItem>
      {showGoogleAds && (
        <NavItem
          data-command={'true'}
          data-command-description={t('Manage Google Ads campaigns')}
          data-command-keywords={'google,ads,search,ppc'}
          data-command-label={t('Google Ads')}
          to={localUrl(`/projects/${eventId}/campaigns/google-ads`)}
        >
          {t('Google Ads')}
        </NavItem>
      )}
      {hasMonetization && (
        <NavItem
          data-command={'true'}
          data-command-description={t('Manage monetization campaigns')}
          data-command-keywords={'monetization,revenue,partner'}
          data-command-label={t('Monetization')}
          to={localUrl(`/projects/${eventId}/campaigns/monetization`)}
        >
          {t('Monetization')}
        </NavItem>
      )}
      {!isPlatformLite && (
        <NavItem
          data-command={'true'}
          data-command-description={t('Other campaign types')}
          data-command-keywords={'other,campaigns,misc'}
          data-command-label={t('Other Campaigns')}
          to={localUrl(`/projects/${eventId}/campaigns/other`)}
        >
          {t('Other')}
        </NavItem>
      )}
    </Nav>
  );
}

export default observer(EventMarketingNav);
